import { useEffect, useRef } from "react";
import * as THREE from "three";

const GROUND_W = 24;
const GROUND_D = 8;
const ROAD_X_START = -6;
const ROAD_X_END = 6;
const ROAD_Z_HALF = 0.85;
const TRUCK_START_X = -5.4;
const TRUCK_END_X = 5.4;
const LOOP_DURATION = 6;
const TEAL = 0x00e0bb;
const TEAL2 = 0x19e5ee;
const WARM = 0xffe9a0;

const makeRoad = () => {
    const group = new THREE.Group();

    // Road surface
    const roadLen = ROAD_X_END - ROAD_X_START;
    const road = new THREE.Mesh(
        new THREE.BoxGeometry(roadLen, 0.02, ROAD_Z_HALF * 2),
        new THREE.MeshStandardMaterial({ color: 0xccd4da, metalness: 0.05, roughness: 0.85 })
    );
    road.position.set((ROAD_X_START + ROAD_X_END) / 2, 0.01, 0);
    group.add(road);

    // Road edges (near and far)
    const edgeMat = new THREE.MeshStandardMaterial({ color: 0xb0bec5 });
    const edgeGeo = new THREE.BoxGeometry(roadLen, 0.025, 0.04);
    const edgeNear = new THREE.Mesh(edgeGeo, edgeMat);
    edgeNear.position.set(0, 0.012, -ROAD_Z_HALF);
    group.add(edgeNear);
    const edgeFar = edgeNear.clone();
    edgeFar.position.set(0, 0.012, ROAD_Z_HALF);
    group.add(edgeFar);

    // Centre dashes
    const dashMat = new THREE.MeshStandardMaterial({ color: 0xdde3e8 });
    const dashGeo = new THREE.BoxGeometry(0.6, 0.025, 0.06);
    const dashCount = 14;
    const dashSpacing = roadLen / dashCount;
    for (let i = 0; i < dashCount; i++) {
        const dash = new THREE.Mesh(dashGeo, dashMat);
        dash.position.set(ROAD_X_START + dashSpacing * i + dashSpacing / 2, 0.013, 0);
        group.add(dash);
    }

    return group;
};

const makeLampPost = () => {
    const group = new THREE.Group();

    // Pole
    const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.025, 0.025, 1.4, 8),
        new THREE.MeshStandardMaterial({ color: 0xa0aab4, metalness: 0.5, roughness: 0.5 })
    );
    pole.position.y = 0.7;
    group.add(pole);

    // Arm
    const arm = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 0.025, 0.025),
        new THREE.MeshStandardMaterial({ color: 0xa0aab4, metalness: 0.5, roughness: 0.5 })
    );
    arm.position.set(0.15, 1.42, 0);
    group.add(arm);

    // Head glow sphere
    const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.07, 12, 12),
        new THREE.MeshStandardMaterial({
            color: WARM,
            emissive: WARM,
            emissiveIntensity: 1.2,
            metalness: 0.2,
            roughness: 0.3,
        })
    );
    head.position.set(0.3, 1.42, 0);
    group.add(head);

    // Soft halo
    const halo = new THREE.Mesh(
        new THREE.SphereGeometry(0.14, 12, 12),
        new THREE.MeshBasicMaterial({ color: WARM, transparent: true, opacity: 0.12 })
    );
    halo.position.set(0.3, 1.42, 0);
    group.add(halo);

    return { group, head };
};

const makeTree = (scale = 1) => {
    const group = new THREE.Group();

    const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05 * scale, 0.07 * scale, 0.5 * scale, 8),
        new THREE.MeshStandardMaterial({ color: 0x8a7060, roughness: 0.9 })
    );
    trunk.position.y = 0.25 * scale;
    group.add(trunk);

    const canopy = new THREE.Mesh(
        new THREE.SphereGeometry(0.38 * scale, 10, 10),
        new THREE.MeshStandardMaterial({ color: 0xb0c09a, roughness: 0.85, metalness: 0.0 })
    );
    canopy.position.y = 0.72 * scale;
    group.add(canopy);

    const topCanopy = new THREE.Mesh(
        new THREE.SphereGeometry(0.22 * scale, 10, 10),
        new THREE.MeshStandardMaterial({ color: 0xbecaaa, roughness: 0.85 })
    );
    topCanopy.position.y = 1.0 * scale;
    group.add(topCanopy);

    return group;
};

const makeWarehouse = () => {
    const group = new THREE.Group();

    const darkMat = (color) =>
        new THREE.MeshStandardMaterial({ color, metalness: 0.4, roughness: 0.55 });

    // Main building body
    const body = new THREE.Mesh(
        new THREE.BoxGeometry(2.8, 1.2, 2.2),
        darkMat(0xc0ccd8)
    );
    body.position.set(0, 0.6, 0);
    group.add(body);

    // Roof cap
    const roof = new THREE.Mesh(
        new THREE.BoxGeometry(3.0, 0.1, 2.4),
        darkMat(0xaebece)
    );
    roof.position.set(0, 1.25, 0);
    group.add(roof);

    // Dock opening on +x face (toward road)
    const dockRecess = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 0.7, 0.6),
        new THREE.MeshStandardMaterial({
            color: TEAL,
            emissive: TEAL,
            emissiveIntensity: 0.6,
            transparent: true,
            opacity: 0.85,
        })
    );
    dockRecess.position.set(1.45, 0.38, 0);
    group.add(dockRecess);

    // Glow plane in front of dock
    const dockGlow = new THREE.Mesh(
        new THREE.PlaneGeometry(0.8, 0.6),
        new THREE.MeshBasicMaterial({
            color: TEAL,
            transparent: true,
            opacity: 0.22,
            side: THREE.DoubleSide,
        })
    );
    dockGlow.rotation.y = Math.PI / 2;
    dockGlow.position.set(1.7, 0.38, 0);
    group.add(dockGlow);

    // Small annex building
    const annex = new THREE.Mesh(
        new THREE.BoxGeometry(1.0, 0.7, 1.0),
        darkMat(0xcad6e2)
    );
    annex.position.set(-1.3, 0.35, -0.9);
    group.add(annex);

    // Crate stack
    const crateMat = darkMat(0xb8c8d4);
    const crateGeo = new THREE.BoxGeometry(0.35, 0.35, 0.35);
    const crateA1 = new THREE.Mesh(crateGeo, crateMat);
    crateA1.position.set(0.9, 0.175, -1.2);
    group.add(crateA1);
    const crateA2 = new THREE.Mesh(crateGeo, crateMat);
    crateA2.position.set(0.9, 0.525, -1.2);
    group.add(crateA2);
    const crateA3 = new THREE.Mesh(crateGeo, crateMat);
    crateA3.position.set(1.25, 0.175, -1.2);
    group.add(crateA3);

    // Parked trailer
    const trailerParked = new THREE.Mesh(
        new THREE.BoxGeometry(1.4, 0.55, 0.55),
        new THREE.MeshStandardMaterial({ color: 0x2a2e36, metalness: 0.4, roughness: 0.5 })
    );
    trailerParked.position.set(-0.3, 0.285, 1.1);
    group.add(trailerParked);

    // Roof beacon sphere
    const beaconSphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 16, 16),
        new THREE.MeshStandardMaterial({
            color: TEAL,
            emissive: TEAL,
            emissiveIntensity: 1.4,
        })
    );
    beaconSphere.position.set(0, 1.36, 0);
    group.add(beaconSphere);

    // Beacon halo torus
    const beaconHalo = new THREE.Mesh(
        new THREE.TorusGeometry(0.18, 0.012, 8, 32),
        new THREE.MeshBasicMaterial({ color: TEAL, transparent: true, opacity: 0.5 })
    );
    beaconHalo.rotation.x = Math.PI / 2;
    beaconHalo.position.set(0, 1.36, 0);
    group.add(beaconHalo);

    // Position entire warehouse to the left
    group.position.set(-8, 0, 0);

    return { group, dockGlow, beaconSphere, beaconHalo };
};

const makeCity = () => {
    const group = new THREE.Group();

    const buildingDefs = [
        { x: -0.5, z: -0.6, w: 1.0, d: 0.9, h: 2.8, color: 0xc2d0da },
        { x: 0.9,  z: -0.3, w: 0.9, d: 0.8, h: 1.9, color: 0xcdd8e2 },
        { x: -0.4, z:  0.7, w: 1.1, d: 0.9, h: 1.4, color: 0xc8d4de },
        { x: 1.1,  z:  0.5, w: 0.7, d: 0.7, h: 2.2, color: 0xbeccd6 },
        { x: -1.5, z:  0.0, w: 0.6, d: 0.7, h: 1.1, color: 0xd0dae4 },
    ];

    const winMat = new THREE.MeshStandardMaterial({
        color: TEAL2,
        emissive: TEAL2,
        emissiveIntensity: 0.8,
        metalness: 0.4,
        roughness: 0.3,
    });

    const windowStrips = [];

    buildingDefs.forEach((def, idx) => {
        const body = new THREE.Mesh(
            new THREE.BoxGeometry(def.w, def.h, def.d),
            new THREE.MeshStandardMaterial({ color: def.color, metalness: 0.35, roughness: 0.5 })
        );
        body.position.set(def.x, def.h / 2, def.z);
        group.add(body);

        const stripCount = idx === 0 ? 4 : 2;
        for (let s = 0; s < stripCount; s++) {
            const strip = new THREE.Mesh(
                new THREE.BoxGeometry(def.w * 0.75, 0.06, 0.025),
                winMat
            );
            strip.position.set(def.x, def.h * 0.25 + s * (def.h / (stripCount + 1)), def.z + def.d / 2 + 0.01);
            group.add(strip);
            windowStrips.push({ mesh: strip, phase: idx * 1.3 + s * 0.7, freq: 1.1 + s * 0.3 });
        }
    });

    // Ground glow ring
    const glowRing = new THREE.Mesh(
        new THREE.RingGeometry(0.5, 2.2, 32),
        new THREE.MeshBasicMaterial({ color: TEAL2, transparent: true, opacity: 0.1, side: THREE.DoubleSide })
    );
    glowRing.rotation.x = -Math.PI / 2;
    glowRing.position.set(0, 0.01, 0);
    group.add(glowRing);

    // Destination beacon on tallest tower (buildingDefs[0])
    const cityBeaconSphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 16, 16),
        new THREE.MeshStandardMaterial({
            color: TEAL2,
            emissive: TEAL2,
            emissiveIntensity: 1.6,
        })
    );
    cityBeaconSphere.position.set(buildingDefs[0].x, buildingDefs[0].h + 0.18, buildingDefs[0].z);
    group.add(cityBeaconSphere);

    const cityBeaconHalo = new THREE.Mesh(
        new THREE.TorusGeometry(0.22, 0.014, 8, 32),
        new THREE.MeshBasicMaterial({ color: TEAL2, transparent: true, opacity: 0.5 })
    );
    cityBeaconHalo.rotation.x = Math.PI / 2;
    cityBeaconHalo.position.copy(cityBeaconSphere.position);
    group.add(cityBeaconHalo);

    group.position.set(8, 0, 0);

    return { group, windowStrips, cityBeaconSphere, cityBeaconHalo, glowRing };
};

const makeTruck = () => {
    const outer = new THREE.Group();
    const group = new THREE.Group();
    outer.add(group);

    const truckColor = TEAL;
    const scale = 1.3;

    // Trailer body
    const trailer = new THREE.Mesh(
        new THREE.BoxGeometry(0.55 * scale, 0.22 * scale, 0.22 * scale),
        new THREE.MeshStandardMaterial({
            color: 0x252830,
            emissive: truckColor,
            emissiveIntensity: 0.15,
            metalness: 0.55,
            roughness: 0.3,
            transparent: true,
            opacity: 1,
        })
    );
    trailer.position.set(-0.18 * scale, 0.16 * scale, 0);
    group.add(trailer);

    // Teal stripe on trailer top
    const stripe = new THREE.Mesh(
        new THREE.BoxGeometry(0.55 * scale, 0.022 * scale, 0.22 * scale),
        new THREE.MeshStandardMaterial({
            color: truckColor,
            emissive: truckColor,
            emissiveIntensity: 0.8,
            transparent: true,
            opacity: 1,
        })
    );
    stripe.position.set(-0.18 * scale, 0.282 * scale, 0);
    group.add(stripe);

    // Cab
    const cab = new THREE.Mesh(
        new THREE.BoxGeometry(0.2 * scale, 0.21 * scale, 0.2 * scale),
        new THREE.MeshStandardMaterial({
            color: 0x1a1d22,
            metalness: 0.7,
            roughness: 0.3,
            transparent: true,
            opacity: 1,
        })
    );
    cab.position.set(0.22 * scale, 0.15 * scale, 0);
    group.add(cab);

    // Windshield
    const windshield = new THREE.Mesh(
        new THREE.BoxGeometry(0.04 * scale, 0.12 * scale, 0.18 * scale),
        new THREE.MeshStandardMaterial({
            color: TEAL2,
            emissive: TEAL2,
            emissiveIntensity: 0.6,
            metalness: 0.9,
            roughness: 0.05,
            transparent: true,
            opacity: 0.85,
        })
    );
    windshield.position.set(0.33 * scale, 0.16 * scale, 0);
    group.add(windshield);

    // Headlights
    const hlMat = new THREE.MeshStandardMaterial({
        color: 0xfff7c2,
        emissive: 0xfff2a0,
        emissiveIntensity: 2.5,
        transparent: true,
        opacity: 1,
    });
    const hlGeo = new THREE.SphereGeometry(0.032 * scale, 10, 10);
    [-1, 1].forEach((side) => {
        const hl = new THREE.Mesh(hlGeo, hlMat);
        hl.position.set(0.36 * scale, 0.1 * scale, side * 0.06 * scale);
        group.add(hl);
    });

    // Headlight cone (forward glow)
    const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.16 * scale, 0.4 * scale, 16, 1, true),
        new THREE.MeshBasicMaterial({
            color: 0xfff2a0,
            transparent: true,
            opacity: 0.14,
            side: THREE.DoubleSide,
        })
    );
    cone.rotation.z = -Math.PI / 2;
    cone.position.set(0.58 * scale, 0.1 * scale, 0);
    group.add(cone);

    // Tail lights
    const tailMat = new THREE.MeshStandardMaterial({
        color: 0xff4040,
        emissive: 0xff2020,
        emissiveIntensity: 1.6,
        transparent: true,
        opacity: 1,
    });
    [-1, 1].forEach((side) => {
        const tail = new THREE.Mesh(
            new THREE.BoxGeometry(0.012 * scale, 0.03 * scale, 0.05 * scale),
            tailMat
        );
        tail.position.set(-0.46 * scale, 0.2 * scale, side * 0.07 * scale);
        group.add(tail);
    });

    // Wheels (6 total)
    const wheelGeo = new THREE.CylinderGeometry(0.058 * scale, 0.058 * scale, 0.05 * scale, 14);
    const wheelMat = new THREE.MeshStandardMaterial({
        color: 0x111114,
        metalness: 0.5,
        roughness: 0.65,
        transparent: true,
        opacity: 1,
    });
    const wheelPositions = [
        [0.22, -0.11], [0.22, 0.11], [-0.1, -0.11], [-0.1, 0.11], [-0.35, -0.11], [-0.35, 0.11],
    ];
    wheelPositions.forEach(([wx, wz]) => {
        const w = new THREE.Mesh(wheelGeo, wheelMat);
        w.rotation.x = Math.PI / 2;
        w.position.set(wx * scale, 0.058 * scale, wz * scale);
        group.add(w);
    });

    // Ground shadow ellipse
    const shadow = new THREE.Mesh(
        new THREE.PlaneGeometry(1.0 * scale, 0.35 * scale),
        new THREE.MeshBasicMaterial({
            color: 0x000000,
            transparent: true,
            opacity: 0.13,
        })
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.set(0, -0.055 * scale, 0);
    outer.add(shadow);

    outer.position.y = 0.058 * scale;

    return outer;
};

const smoothstep = (t) => t * t * (3 - 2 * t);

export const Hero3D = () => {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const width = mount.clientWidth;
        const height = mount.clientHeight;

        // === Scene ===
        const scene = new THREE.Scene();

        // === Isometric Camera ===
        const frustumSize = 14;
        const aspect = width / height;
        const camera = new THREE.OrthographicCamera(
            (-frustumSize * aspect) / 2,
            (frustumSize * aspect) / 2,
            frustumSize / 2,
            -frustumSize / 2,
            0.1,
            200
        );
        camera.position.set(10, 10, 10);
        camera.lookAt(0, 0, 0);

        // === Renderer ===
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
        renderer.setSize(width, height);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        // === Lights ===
        scene.add(new THREE.AmbientLight(0xffffff, 0.9));
        const sun = new THREE.DirectionalLight(0xfff8f0, 0.8);
        sun.position.set(8, 14, 6);
        scene.add(sun);
        const warehouseAccent = new THREE.PointLight(TEAL, 1.2, 18);
        warehouseAccent.position.set(-7, 2, 0);
        scene.add(warehouseAccent);
        const cityAccent = new THREE.PointLight(TEAL2, 1.0, 18);
        cityAccent.position.set(7, 3, 0);
        scene.add(cityAccent);

        // === Ground ===
        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(GROUND_W, GROUND_D),
            new THREE.MeshStandardMaterial({
                color: 0xeaecef,
                metalness: 0.02,
                roughness: 0.9,
                transparent: true,
                opacity: 0.7,
            })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.set(0, 0, 0);
        scene.add(ground);

        // Grid
        const grid = new THREE.GridHelper(GROUND_W, 24, TEAL, 0xd0d6de);
        grid.material.transparent = true;
        grid.material.opacity = 0.18;
        grid.position.y = 0.005;
        scene.add(grid);

        // === Road ===
        const road = makeRoad();
        scene.add(road);

        // === Lamp posts (near side, z = -ROAD_Z_HALF - 0.3) ===
        const lampHeads = [];
        const lampXPositions = [-4, -2, 0, 2, 4];
        lampXPositions.forEach((x) => {
            const { group, head } = makeLampPost();
            group.position.set(x, 0.01, -ROAD_Z_HALF - 0.3);
            scene.add(group);
            lampHeads.push(head);
        });

        // === Trees (far side, z = ROAD_Z_HALF + 0.5) ===
        const treeXPositions = [-3.5, -1.5, 0.5, 2.5, 4.5];
        treeXPositions.forEach((x, i) => {
            const tree = makeTree(0.85 + (i % 2) * 0.2);
            tree.position.set(x, 0.01, ROAD_Z_HALF + 0.7 + (i % 3) * 0.25);
            scene.add(tree);
        });

        // === Warehouse ===
        const { group: warehouseGroup, dockGlow, beaconSphere: wBeacon, beaconHalo: wHalo } = makeWarehouse();
        scene.add(warehouseGroup);

        // === City ===
        const {
            group: cityGroup,
            windowStrips,
            cityBeaconSphere: cBeacon,
            cityBeaconHalo: cHalo,
            glowRing,
        } = makeCity();
        scene.add(cityGroup);

        // === Truck ===
        const truck = makeTruck();
        truck.position.set(TRUCK_START_X, 0, 0);
        scene.add(truck);

        // === Particles ===
        const PARTICLE_COUNT = 60;
        const pPos = new Float32Array(PARTICLE_COUNT * 3);
        for (let i = 0; i < PARTICLE_COUNT; i++) {
            pPos[i * 3]     = (Math.random() - 0.5) * GROUND_W;
            pPos[i * 3 + 1] = Math.random() * 5 + 0.3;
            pPos[i * 3 + 2] = (Math.random() - 0.5) * GROUND_D;
        }
        const pGeo = new THREE.BufferGeometry();
        pGeo.setAttribute("position", new THREE.BufferAttribute(pPos, 3));
        const pMat = new THREE.PointsMaterial({
            color: TEAL2,
            size: 0.055,
            transparent: true,
            opacity: 0.35,
            sizeAttenuation: true,
        });
        const particles = new THREE.Points(pGeo, pMat);
        scene.add(particles);

        // === Animate ===
        const clock = new THREE.Clock();
        let frameId;
        const animate = () => {
            const t = clock.getElapsedTime();
            const loopT = (t % LOOP_DURATION) / LOOP_DURATION;

            // --- Truck position ---
            // 0–0.08: ease in, 0.08–0.90: cruise, 0.90–0.96: ease out, 0.96–1.0: invisible reset
            let truckX;
            let truckVisible = true;

            if (loopT < 0.08) {
                const p = smoothstep(loopT / 0.08);
                truckX = TRUCK_START_X + p * (TRUCK_END_X - TRUCK_START_X) * 0.08;
            } else if (loopT < 0.90) {
                const p = (loopT - 0.08) / 0.82;
                truckX = TRUCK_START_X + (TRUCK_END_X - TRUCK_START_X) * (0.08 + p * 0.82);
            } else if (loopT < 0.96) {
                const p = smoothstep((loopT - 0.90) / 0.06);
                truckX = TRUCK_START_X + (TRUCK_END_X - TRUCK_START_X) * (0.90 + p * 0.06);
            } else {
                truckX = TRUCK_START_X;
                truckVisible = false;
            }

            truck.position.x = truckX;
            truck.visible = truckVisible;

            // --- Warehouse beacon pulse ---
            const wPulse = 1 + Math.sin(t * 2.1) * 0.15;
            wBeacon.scale.setScalar(wPulse);
            wHalo.scale.setScalar(1 + Math.sin(t * 2.1) * 0.22);
            wHalo.rotation.y = t * 0.5;

            // --- Dock glow brightens when truck is near departure (loopT < 0.12) ---
            dockGlow.material.opacity = loopT < 0.12
                ? 0.22 + smoothstep(1 - loopT / 0.12) * 0.35
                : 0.22;

            // --- City beacon brightens on truck arrival (loopT > 0.82) ---
            const arrivalStrength = loopT > 0.82 ? smoothstep((loopT - 0.82) / 0.14) : 0;
            const cPulse = 1 + Math.sin(t * 1.8) * 0.18 + arrivalStrength * 0.25;
            cBeacon.scale.setScalar(cPulse);
            cBeacon.material.emissiveIntensity = 1.6 + arrivalStrength * 1.4;
            cHalo.scale.setScalar(1 + Math.sin(t * 1.8) * 0.2 + arrivalStrength * 0.3);
            cHalo.rotation.y = t * 0.6;
            glowRing.material.opacity = 0.1 + arrivalStrength * 0.15;

            // --- Lamp post head breathe (staggered) ---
            lampHeads.forEach((head, i) => {
                head.material.emissiveIntensity = 1.0 + Math.sin(t * 0.9 + i * 1.1) * 0.3;
            });

            // --- Window strip flicker ---
            windowStrips.forEach(({ mesh, phase, freq }) => {
                mesh.material.emissiveIntensity = 0.6 + Math.sin(t * freq + phase) * 0.25;
            });

            // --- Particles drift upward ---
            const pArray = pGeo.attributes.position.array;
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                pArray[i * 3 + 1] += 0.008;
                if (pArray[i * 3 + 1] > 5.3) pArray[i * 3 + 1] = 0.3;
            }
            pGeo.attributes.position.needsUpdate = true;

            renderer.render(scene, camera);
            frameId = requestAnimationFrame(animate);
        };
        animate();

        // === Resize ===
        const onResize = () => {
            const w = mount.clientWidth;
            const h = mount.clientHeight;
            if (!w || !h) return;
            const a = w / h;
            camera.left = (-frustumSize * a) / 2;
            camera.right = (frustumSize * a) / 2;
            camera.top = frustumSize / 2;
            camera.bottom = -frustumSize / 2;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        };
        window.addEventListener("resize", onResize);

        return () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener("resize", onResize);
            if (renderer.domElement.parentNode === mount)
                mount.removeChild(renderer.domElement);
            renderer.dispose();
            scene.traverse((obj) => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                    if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
                    else obj.material.dispose();
                }
            });
        };
    }, []);

    return (
        <div
            ref={mountRef}
            data-testid="hero-3d-canvas"
            className="absolute inset-0 w-full h-full"
        />
    );
};

export default Hero3D;
